import * as THREE from 'three';
import { animateTransform } from '../scene-transforms.js';
import type { AnimatedProp } from './coast-props.js';
import { coastSceneryResources } from './coast-obstacles.js';
import { SceneryBatches, seededRandom } from './scenery-batches.js';
import { createBushGeometry, createGrassClump, palette, woodlandPine } from './vegetation.js';
import { createTreeWind, createWindMaterial } from './wind-material.js';
import { weatheredMaterial } from './prop-detail.js';
import { naturalDetails } from './natural-details.js';

export { createFishingBoat } from './fishing-boat.js';
import { createFishingBoat } from './fishing-boat.js';

/** Spaced, grounded mineral shafts: tall spires, a fan, or a low seam. */
export function createCrystalCluster(random: () => number, variant = 0) {
  const group = new THREE.Group();
  const colors = [0x70b9ca, 0x997ebf, 0x72a999], emission = [0x237c94, 0x644090, 0x267767];
  const mineral = new THREE.MeshStandardMaterial({ color: colors[variant % 3], emissive: emission[variant % 3], emissiveIntensity: .5, metalness: .2, roughness: .3, flatShading: true });
  const stone = weatheredMaterial(0x55534d, 'stone', { flatShading: true });
  const layout = variant === 1 ? [[-1.8, .3], [-.7, -.5], [.65, -.6], [1.8, .1], [.2, 1.3]] : variant === 2 ? [[-2, -.4], [-.8, .3], [.4, -.1], [1.6, .4], [2.4, -.4], [.4, 1.2]] : [[-1.7, .1], [.0, -.8], [1.65, -.1], [-.4, 1.3]];
  for (const [i, [x, z]] of layout.entries()) {
    const h = variant === 2 ? .65 + random() * .85 : 1.8 + random() * 2.3, r = .22 + random() * .2;
    const pivot = new THREE.Group(); pivot.position.set(x, .02, z); pivot.rotation.set((random()-.5)*.16, random()*6, variant === 1 ? (i-2)*.14 : (random()-.5)*.18); group.add(pivot);
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r*.84,r,h*.72,6),mineral); shaft.position.y=h*.36; shaft.castShadow=true; pivot.add(shaft);
    const tip = new THREE.Mesh(new THREE.ConeGeometry(r*.84,h*.28,6),mineral); tip.position.y=h*.86; tip.castShadow=true; pivot.add(tip);
    const foot = new THREE.Mesh(new THREE.DodecahedronGeometry(r*1.7,0),stone); foot.scale.y=.35; foot.position.set(x,r*.35,z); foot.castShadow=foot.receiveShadow=true; group.add(foot);
  }
  return group;
}

/** Scattered ground dressing is drawn instanced across the whole map; the viewer shows one hand-sized specimen of each with the same geometry and palette. */
export function createDressingSpecimen(scene: THREE.Scene, kind: string, variant = 0): AnimatedProp | undefined {
  const random = seededRandom(kind.length * 7919 + kind.charCodeAt(0) * 104729);
  const pick = (list: number[]) => list[Math.floor(random() * list.length)];
  const resources = coastSceneryResources(), { geometries } = resources, shared = resources.material;
  const batches = new SceneryBatches(scene);
  const grassWind = createWindMaterial(.3, .5, 1, .62, 1.22), bushWind = createWindMaterial(.14, 0, 1, .85, 1.08);
  const canopyWind = createWindMaterial(.12, 0, 1.4, .85, 1.08), trees = createTreeWind(.18);
  bushWind.material.vertexColors = true; canopyWind.material.vertexColors = true;
  const winds = [grassWind, bushWind, canopyWind, trees];
  const clump = createGrassClump(), bushGeometry = createBushGeometry();
  const blade = (x: number, z: number, h: number, s: number, color: number) => batches.add('grass', clump, grassWind.material, x, h / 2, z, s * 1.5, h, s * 1.5, random() * 6, color, false);
  const bush = (x: number, z: number, s: number, color: number) => batches.add('bush', bushGeometry, canopyWind.material, x, 0, z, s, s, s, random() * 6, color, true);
  const pebble = (x: number, z: number, s: number) => batches.add('pebble', geometries.rock, shared, x, s * .22, z, s, s * .62, s * .8, random() * 6, pick(palette.stone), true);
  const flower = (x: number, z: number, tone: number) => details.flower(x, z, tone);
  const timber = weatheredMaterial(0xffffff, 'wood', { flatShading: true });
  const details = naturalDetails(batches, timber, bushWind.material, trees, random);
  const ground = (radius: number, color: number) => {
    const patch = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, .06, 24), new THREE.MeshStandardMaterial({ color, roughness: 1 }));
    patch.userData.propBackdrop = true; patch.position.y = -.03; patch.receiveShadow = true; scene.add(patch);
  };

  switch (kind) {
    case 'grass-tufts':
      ground(3.2, 0x6c8a3a);
      for (let i = 0; i < 70; i++) { const a = random() * 6.28, d = Math.sqrt(random()) * 2.8; blade(Math.cos(a) * d, Math.sin(a) * d, .25 + random() * .6, .35 + random() * .35, pick(palette.meadowGrass)); }
      break;
    case 'wheat-rows':
      ground(3.4, 0x805f3e);
      for (let row = -2; row <= 2; row++) for (let z = -2.6; z <= 2.6; z += 1) blade(row * 1.1, z + random() * .3, .7 + random() * .35, .2 + random() * .1, pick(palette.wheat));
      break;
    case 'bushes':
      ground(3, 0x6c8a3a);
      bush(-.9, .2, 1.1, pick(palette.bush)); bush(.8, -.5, .7, pick(palette.bush)); bush(.5, 1, .5, pick(palette.heather));
      break;
    case 'wildflowers': {
      ground(3, 0x6c8a3a); const tone = pick(palette.flower);
      for (let i = 0; i < 30; i++) flower((random() - .5) * 5, (random() - .5) * 3.5, i % 5 ? tone : pick(palette.flower));
      for (let i = 0; i < 12; i++) blade((random() - .5) * 5, (random() - .5) * 3.5, .3 + random() * .3, .3, pick(palette.meadowGrass));
      break;
    }
    case 'reeds': {
      ground(3.2, 0x4f6249);
      batches.add('puddle', new THREE.CylinderGeometry(1, 1, .02, 10), shared, 0, .045, 0, 2.2, 1, 1.6, 0, 0x35575c, false);
      for (let i = 0; i < 14; i++) { const a = random() * 6.28, d = 1.5 + random() * 1.2; blade(Math.cos(a) * d, Math.sin(a) * d, .8 + random() * .9, .22 + random() * .3, pick(palette.reed)); }
      for (let i = 0; i < 6; i++) { const a = random() * 6.28, d = 1.7 + random() * .9; details.reed(Math.cos(a) * d, Math.sin(a) * d, .8 + random() * .5); }
      break;
    }
    case 'mushrooms': {
      ground(2.6, 0x44583f);
      for (let m = 0; m < 12; m++) {
        const a = m / 12 * Math.PI * 2 + random() * .3, d = 1 + random() * .7;
        details.mushroom(Math.cos(a) * d, Math.sin(a) * d, .14 + random() * .16, m % 3);
      }
      break;
    }
    case 'ferns': {
      ground(3, 0x44583f);
      for (let f = 0; f < 6; f++) details.fern((random() - .5) * 3.4, (random() - .5) * 3.4, .7 + random() * .6, pick([0x4f6b3c, 0x5d7a43, 0x46613a]));
      break;
    }
    case 'fallen-log': {
      ground(4, 0x44583f);
      details.log(0, 0, 5.5, .38, .3);
      break;
    }
    case 'stumps':
      ground(3, 0x44583f);
      for (const [x, z, w] of [[-1, .3, .8], [.7, -.6, .6], [.9, .9, .45]]) details.stump(x, z, w);
      break;
    case 'outcrop':
      ground(3.4, 0x6c8a3a);
      for (const [x, z, s] of [[-.8, .2, .55], [.5, -.6, .45], [.9, .7, .35], [-.2, 1, .3]]) {
        batches.add('outcrop', geometries.rock, shared, x, s * .1, z, s * 1.3, s * .6, s, random() * 6, pick([0x7e7a67, 0x6f6c5c, 0x898470]), true);
        batches.add('lichen', geometries.rock, shared, x, s * .42, z, s * .55, s * .14, s * .5, random() * 6, pick([0x7c7d44, 0x6c7e44, 0x8e8a5c]), false);
        for (let g = 0; g < 5; g++) blade(x + (random() - .5) * s * 2.4, z + (random() - .5) * s * 2.4, .3 + random() * .4, .3, pick(palette.meadowGrass));
      }
      break;
    case 'lone-tree': {
      ground(4, 0x6c8a3a);
      details.tree(0, 0, 9);
      break;
    }
    case 'woodland-pine': {
      ground(3.4, 0x44583f);
      const h = 14, s = 3;
      details.roots(0, 0, 1.1);
      woodlandPine(batches, geometries, timber, trees, 0, 0, h, s, 0, pick(palette.pine), pick(palette.pine));
      break;
    }
    case 'driftwood': {
      ground(4, 0xc2a97f);
      for (const [x, z, length, turn] of [[-.8, .4, 3.2, .3], [1, -.8, 2.2, 1.2], [.3, 1.4, 2.6, -.4]]) details.driftwood(x, z, length, turn);
      for (const [x, z] of [[-1.8, -1.4], [1.6, 1.2]]) details.kelp(x, z, 1.2);
      for (let i = 0; i < 5; i++) blade((random() - .5) * 6, (random() - .5) * 2 - 2, .45 + random() * .6, .5, pick(palette.reed));
      break;
    }
    case 'pebbles-shells':
      ground(3, 0xc2a97f);
      for (let i = 0; i < 22; i++) pebble((random() - .5) * 5, (random() - .5) * 5, .15 + random() * .5);
      for (let i = 0; i < 6; i++) batches.add('shell', geometries.rock, shared, (random() - .5) * 4.5, .05, (random() - .5) * 4.5, .22, .08, .16, random() * 6, 0xe6d9c4, false);
      break;
    case 'sea-stack': {
      const water = new THREE.Mesh(new THREE.CylinderGeometry(7, 7, .04, 28), new THREE.MeshStandardMaterial({ color: 0x1f5963, roughness: .2, metalness: .2 })); water.userData.propBackdrop = true; water.position.y = .02; scene.add(water);
      const rock = new THREE.DodecahedronGeometry(1, 0), s = 5;
      batches.add('sea-stack', rock, shared, 0, s * .9, 0, s, s * 2.4, s * .9, 1, 0x5d5964, true);
      batches.add('sea-stack-cap', rock, shared, 0, s * 2.6, 0, s * .55, s * .5, s * .55, 2, 0x8a8470, false);
      for (const [x, z, k] of [[5, 2, 1.6], [-4.5, 3, 2.2]]) batches.add('skerry', rock, shared, x, k * .25, z, k * 1.4, k * .6, k, 3, 0x7d756b, false);
      break;
    }
    case 'fishing-boat': {
      const water = new THREE.Mesh(new THREE.CylinderGeometry(7, 7, .04, 28), new THREE.MeshStandardMaterial({ color: 0x1f5963, roughness: .2, metalness: .2 })); water.userData.propBackdrop = true; water.position.y = .02; scene.add(water);
      const { group, light } = createFishingBoat(); group.position.y = .1; group.rotation.y = -.35; scene.add(group);
      details.disposeUnused(); batches.flush();
      return { update: time => { group.position.y = .1 + Math.sin(time * .9) * .08; group.rotation.z = Math.sin(time * .7) * .025; light.scale.setScalar(.85 + Math.sin(time * 5) * .15); } };
    }
    case 'cave-crystals': {
      ground(4, 0x4a4540);
      const cluster = createCrystalCluster(random, variant); scene.add(cluster);
      for (const crystal of cluster.children) crystal.castShadow = true;
      break;
    }
    default: throw new Error(`Unknown dressing specimen ${kind}`);
  }
  details.disposeUnused(); batches.flush();
  return { update: time => winds.forEach(wind => wind.update(time)) };
}
